import { db } from '../../lib/firebase';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    query,
    where,
    serverTimestamp
} from 'firebase/firestore';
import { UniversalEntity, EntityType } from '../../../../shared/src/models/universal_entity';
import { getModulesFor } from '../../config/moduleMatrix';

export class EntityService {
    private collectionRef = collection(db, 'entities');

    async createEntity(clientId: string, data: Partial<UniversalEntity>): Promise<string> {
        const newDocRef = doc(this.collectionRef);
        const id = newDocRef.id;

        const moduleConfig = data.type ? getModulesFor(data.type) : null;
        const enabledModules = moduleConfig
            ? Object.entries(moduleConfig)
                .filter(([_, config]) => config !== null && config.required)
                .map(([moduleId]) => moduleId)
            : [];

        const entity: UniversalEntity = {
            id,
            clientId,
            legalName: data.legalName || 'New Entity',
            type: data.type || 'INDIVIDUAL', // Default or throw
            taxClassification: data.taxClassification || 'DISREGARDED',
            status: 'ACTIVE',
            principalAddress: data.principalAddress || { street1: '', city: '', state: '', zip: '' },
            formationState: data.formationState || 'DE',
            fiscalYearEnd: data.fiscalYearEnd || '12-31',
            ...data,
            // Auto-enable required modules if not specified
            enabledModules: data.enabledModules && data.enabledModules.length > 0
                ? data.enabledModules
                : (enabledModules as any),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        await setDoc(newDocRef, entity);

        // TODO: Initialize sub-collections or default documents for enabled modules?
        // For now, we just create the shell.

        return id;
    }

    async getEntity(id: string): Promise<UniversalEntity | null> {
        const docRef = doc(this.collectionRef, id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
            return snap.data() as UniversalEntity;
        }
        return null;
    }

    async updateEntity(id: string, updates: Partial<UniversalEntity>): Promise<void> {
        const docRef = doc(this.collectionRef, id);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp()
        });
    }

    async listEntities(clientId: string): Promise<UniversalEntity[]> {
        const q = query(this.collectionRef, where('clientId', '==', clientId));
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data() as UniversalEntity);
    }

    async listEntitiesByType(clientId: string, type: EntityType): Promise<UniversalEntity[]> {
        const q = query(
            this.collectionRef,
            where('clientId', '==', clientId),
            where('type', '==', type)
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data() as UniversalEntity);
    }
}

export const entityService = new EntityService();
